<template>
  <div class="glass-tabs" :class="[`variant-${variant}`, { 'is-vertical': vertical }]">
    <!-- Tab Headers -->
    <div class="tabs-header" role="tablist">
      <button
        v-for="(tab, index) in tabs"
        :key="index"
        type="button"
        role="tab"
        :aria-selected="activeIndex === index"
        :aria-controls="`tab-panel-${index}`"
        class="tab-button"
        :class="{ 'is-active': activeIndex === index, 'is-disabled': tab.disabled }"
        @click="!tab.disabled && selectTab(index)"
      >
        <i v-if="tab.icon" :class="tab.icon" class="tab-icon"></i>
        <span class="tab-title">{{ tab.title }}</span>
      </button>
    </div>

    <!-- Tab Panels -->
    <div class="tabs-content" :class="contentClass">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GlassTabs',
  emits: ['update:modelValue', 'change'],
  provide() {
    return {
      glassTabs: this
    };
  },
  props: {
    modelValue: {
      type: Number,
      default: 0
    },
    variant: {
      type: String,
      default: 'default',
      validator: (val) => ['default', 'pills', 'underline'].includes(val)
    },
    vertical: {
      type: Boolean,
      default: false
    },
    contentClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      tabs: [],
      activeIndex: this.modelValue
    };
  },
  watch: {
    modelValue(newVal) {
      this.activeIndex = newVal;
    },
    activeIndex(newVal) {
      this.$emit('update:modelValue', newVal);
    }
  },
  methods: {
    registerTab(tab) {
      this.tabs.push(tab);
    },
    unregisterTab(tab) {
      const index = this.tabs.indexOf(tab);
      if (index > -1) {
        this.tabs.splice(index, 1);
      }
    },
    selectTab(index) {
      this.activeIndex = index;
      this.$emit('change', index);
    },
    isActiveTab(tab) {
      return this.tabs.indexOf(tab) === this.activeIndex;
    }
  }
};
</script>

<style scoped>
.glass-tabs {
  display: flex;
  flex-direction: column;
}

.glass-tabs.is-vertical {
  flex-direction: row;
}

.tabs-header {
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.is-vertical .tabs-header {
  flex-direction: column;
  border-bottom: none;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  padding-right: 1rem;
  padding-bottom: 0;
  margin-bottom: 0;
  margin-right: 1rem;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tab-button:hover:not(.is-disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-text);
}

.tab-button.is-active {
  background: rgba(59, 136, 165, 0.15);
  color: var(--theme-primary);
}

.tab-button.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Variant: Pills */
.variant-pills .tab-button.is-active {
  background: var(--theme-primary);
  color: white;
}

/* Variant: Underline */
.variant-underline .tabs-header {
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  gap: 0;
}

.variant-underline .tab-button {
  border-radius: 0;
  margin-bottom: -2px;
  border-bottom: 2px solid transparent;
}

.variant-underline .tab-button.is-active {
  background: transparent;
  border-bottom-color: var(--theme-primary);
}

.tab-icon {
  font-size: 1rem;
}

.tabs-content {
  flex: 1;
}
</style>
